import React, { FC, useMemo } from 'react';

import { EvSection, EvSpinner } from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_ALL_ISSUES_TAB_DAID,
	MOBILE_SDK_TEST_ISSUES_TABLE_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileSdkTestAllIssuesQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestAllIssuesQuery';
import { MobileSdkSingleTestFiltersSection } from 'src/apps/mobile-sdk/pages/single-test/filters-section/MobileSdkSingleTestFiltersSection';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import {
	getMobileSdkAllIssuesColumns,
	MOBILE_SDK_ALL_ISSUES_TABLE_OPTIONS
} from './MobileSdkTestAllIssuesTableHelper';

import './MobileSdkTestAllIssues.scss';

interface IMobileSdkTestAllIssuesProps {
	testId: string;
}

export const MobileSdkTestAllIssues: FC<IMobileSdkTestAllIssuesProps> = ({ testId }) => {
	const { getTableStateById, onFilterChange } = useTableState();
	const tableId = useMemo(
		() => buildFullTableId(MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID, testId),
		[testId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const issuesQuery = useMobileSdkTestAllIssuesQuery({
		testId,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filters
	});

	if (issuesQuery?.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_SDK}
			/>
		);
	}

	if (!issuesQuery?.isLoading && !issuesQuery?.isPreviousData && !issuesQuery?.data) {
		return <EvSpinner />;
	}

	const { total = 0, unfilteredTotal = 0, rows = [] } = issuesQuery.data ?? {};

	return (
		<EvSection
			className="mobile-sdk-test-all-issues-view"
			dataAutomationId={MOBILE_SDK_TEST_ALL_ISSUES_TAB_DAID}
		>
			<MobileSdkSingleTestFiltersSection
				testId={testId}
				filters={filters}
				ariaLabel="Mobile SDK Test's all issues filters"
				onFilterChange={onFilterChange}
				tableId={tableId}
			/>
			<EvSection ariaLabel="Mobile SDK Test's all issues table" skipLinkId="main-section">
				<PlatformTable
					dataAutomationId={MOBILE_SDK_TEST_ISSUES_TABLE_DAID}
					isTableLoading={issuesQuery.isLoading || issuesQuery.isPreviousData}
					tableId={tableId}
					tableData={rows}
					totalCount={unfilteredTotal}
					totalPaginationableResults={total}
					className="single-test-all-issues-table"
					persistTableState
					columns={getMobileSdkAllIssuesColumns(testId)}
					options={MOBILE_SDK_ALL_ISSUES_TABLE_OPTIONS}
					withUrlSwitcher={false}
				/>
			</EvSection>
		</EvSection>
	);
};
